import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/20/solid';

import AstroImage from '@/components/ui/custom/AstroImage';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Button, Progress } from '@/components/ui/daisy';
import { ContinuityLesson } from '@/utils/student-dashboard/continuityLesson';
import { UnitProgress } from '@/utils/student-dashboard/unitProgress';
import { formatLessonName, formatLessonNamePrefix } from '@/utils/lesson/name';
import { formatUnitNamePrefix } from '@/utils/unit/name';

export type ContinuityCardProps = {
  lesson: ContinuityLesson;
  progress: UnitProgress;
  href?: string;
  children?: ReactNode;
};

export default function ContinuityCard({
  lesson,
  progress,
  href,
  children,
}: ContinuityCardProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'studentDashboard.continuity',
  });
  const lessonName = formatLessonName(lesson);
  const title = lesson.unitOrder
    ? formatUnitNamePrefix({ unitOrder: lesson.unitOrder })
    : lessonName;

  return (
    <article
      data-testid="continuityCard"
      className="flex min-w-0 flex-col gap-4 rounded-2xl bg-base-200 p-4"
    >
      <div className="flex items-center gap-3">
        <AstroImage
          zoomable
          src={lesson.bannerImg}
          alt={t('bannerAlt', { lesson: title })}
          className="size-15 shrink-0 rounded-lg object-cover"
        />
        <div className="flex min-w-0 flex-col gap-1">
          <h3 className="truncate text-sm font-semibold text-base-content">
            {title}
          </h3>
          <ConditionalRenderer condition={!!lesson.unitOrder}>
            <p className="truncate text-xs text-neutral">{lessonName}</p>
          </ConditionalRenderer>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-primary">
            {t('completedPercentage', { percentage: progress.percentage })}
          </span>
          <span className="text-xs text-neutral">
            {progress.completed}/{progress.total}
          </span>
        </div>
        <Progress
          color="primary"
          value={progress.percentage}
          className="h-2 w-full"
        />
      </div>
      <ConditionalRenderer condition={!!href}>
        <Link to={href ?? ''} className="w-full">
          <Button
            color="primary"
            shape="block"
            tabIndex={-1}
            className="rounded-lg"
          >
            {`${t('continueLesson')} ${formatLessonNamePrefix({
              lessonOrder: lesson.order,
            })}`}
            <ArrowRightIcon className="size-5" />
          </Button>
        </Link>
      </ConditionalRenderer>
      {children}
    </article>
  );
}
